<template>
    <section name="request overview" class="gap-sm flex h-full flex-col">
        <!-- DataTable with Infinite Scroll -->
        <DataTable
            :data="sortedItems"
            :columns="requestColumns"
            :loading="isLoading"
            :enable-search="false"
            :page-size="sortedItems.length || baseItemCount"
            search-placeholder="Search in filtered results"
            title="Latest tickets"
            :grid-mode="showDisplayToggle ? undefined : !isDesktop"
            :mobile-mode="showDisplayToggle ? 'cards' : 'auto'"
            :empty-state-config="{
                title: 'No requests found',
                message: noResultMessage ?? defaultNoResultText,
            }"
            @row-click="routeToDetails"
        >
            <template v-if="showFilters" #table-header-filters>
                <div name="filters" class="gap-xs flex items-center">
                    <RequestFilterComp
                        v-if="filterSetupComplete"
                        :selected-filter="selectedFilter"
                        :disabled="isLoading || isFiltering"
                        @apply="updateAndApplyFilter($event)"
                        @reset="clear"
                    />

                    <Button
                        v-if="!isDesktop && showCreateRequest"
                        class="btn col-span-2 h-12 w-12 justify-self-end"
                        @click="router.push({ name: 'service-desks' })"
                    >
                        <i class="i-plus block" />
                    </Button>
                </div>
            </template>

            <template #custom-grid-mode="{ data: rows, handleRowClick }">
                <div class="flex flex-col" :class="{ 'gap-xs': !compactMobileCards }">
                    <!-- The whole card is the tap target; the arrow icons below
                         are affordance only, so the handler lives here. -->
                    <div
                        v-for="ticket in rows"
                        :key="ticket.id"
                        class="gap-md bg-lightest flex cursor-pointer flex-col rounded-md"
                        :class="{ 'p-sm bg-primary': !compactMobileCards }"
                        @click="handleRowClick(ticket)"
                    >
                        <template v-if="!compactMobileCards">
                            <div class="flex items-center justify-between">
                                <div class="gap-xs flex items-center text-base">
                                    <AvatarArea :first-name="ticket.requester" :size="'x-small'" />
                                    <p>{{ ticket.requester }}</p>
                                </div>
                                <i class="i-arrow-right size-5" />
                            </div>

                            <div class="pl-md gap-2xs flex flex-col">
                                <p>{{ ticket.summary }}</p>
                                <p class="text-(--text-muted)">{{ ticket.type }}</p>
                            </div>

                            <div class="grid grid-cols-4 gap-x-6 gap-y-2">
                                <!-- Labels Row -->
                                <p class="col-span-2 text-(--text-muted)">Status</p>
                                <p class="justify-self-end text-(--text-muted)">Created</p>
                                <p class="justify-self-end text-(--text-muted)">Updated</p>

                                <!-- Values Row -->
                                <TagComponent :value="ticket.status" :show-dot="true" />

                                <p class="justify-self-end">
                                    {{ formatDate(ticket.created_at) }}
                                </p>
                                <p class="justify-self-end">
                                    {{ formatDate(ticket.updated_at) }}
                                </p>
                            </div>
                        </template>
                        <template v-else>
                            <div class="py-sm flex justify-between border-b border-dotted border-(--border-strong)">
                                <div class="gap-2xs flex flex-col">
                                    <p>{{ ticket.summary }}</p>
                                    <p class="text-(--text-muted)">{{ ticket.type }}</p>
                                </div>
                                <div class="gap-sm flex items-center">
                                    <TagComponent :value="ticket.status" class-name="col-span-2" :show-dot="true" />
                                    <i class="i-arrow-right size-5" />
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </template>
        </DataTable>

        <!-- Loading indicator for infinite scroll -->
        <div v-if="isLoadingMore" class="flex justify-center py-4">
            <p class="text-(--text-muted)">Loading more...</p>
        </div>

        <!-- Intersection Observer Sentinel -->
        <div v-if="hasMoreData && !isLoadingMore && sortedItems.length > 0" ref="sentinelRef" class="h-1"></div>

        <!-- End of results indicator -->
        <div v-if="!hasMoreData && sortedItems.length > 0" class="flex justify-center py-4">
            <p class="text-(--text-muted)">No more results</p>
        </div>
        <div
            v-if="!isLoading && hasMoreData && sortedItems.length > 0 && isDesktop && clickShowMore"
            class="gap-xs mt-sm flex items-center justify-center"
        ></div>
    </section>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue';
import { useSupportStore } from '@/stores/support';
import { formatDate, translateRequestDatatoRequestType } from '@/stores/helpers';
import router from '@/router';
import { useRoute, type LocationQuery } from 'vue-router';
import DataTable from '@/components/tables/DataTable.vue';
import {
    createTextColumn,
    createStatusColumn,
    createDateColumn,
    createResponseTimeColumn,
} from '@/components/tables/columns/factories';
import type { Request, RequestFilter, RequestFilterData, Option } from '@/stores/store-interfaces';
import { useIsDesktop } from '@/composables/isDesktop';
import AvatarArea from '../avatar-area.vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import RequestFilterComp from '@/components/support/request-filter.vue';

const route = useRoute();
const supportStore = useSupportStore();
const requests = ref<Request[]>();
const isLoading = ref<boolean>(true);
const filterSetupComplete = ref(false);
const totalRequests = ref<number>(0);
const defaultNoResultText = 'There are not results based on your current filter. Please update your filter criteria';

// Pagination variables
const baseItemCount = 20;
const nextPageToken = ref<string | null>(null);
const hasMoreData = ref(true);
const isFiltering = ref(false);
const isLoadingMore = ref(false);
const sentinelRef = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const isDesktop = useIsDesktop();

const selectedFilter = ref<RequestFilter>({
    serviceDesks: [],
    requestTypes: [],
    statuses: [],
    otherFilters: [],
    referenceSearch: '',
    summarySearch: '',
});

const selectedFilterData = ref<RequestFilterData>();

const emit = defineEmits(['requestLimitReached', 'endLoading', 'noResults']);

const props = withDefaults(
    defineProps<{
        data?: Request[];
        itemCount?: number;
        showFilters?: boolean;
        noResultMessage?: string;
        clickShowMore?: boolean;
        showCreateRequest?: boolean;
        compactMobileCards?: boolean;
        enablePolling?: boolean;
        /**
         * ITDEVELOP-3170: let the user switch table/cards and pick columns via
         * DataTable's display controls. Off for embedded fixed lists (the
         * dashboard), on for the full /support page.
         */
        showDisplayToggle?: boolean;
    }>(),
    {
        showFilters: false,
        clickShowMore: false,
        noResultMessage: '',
        showCreateRequest: false,
        enablePolling: false,
    },
);

const requestColumns = [
    createTextColumn<Request>('reference', 'Reference'),
    createTextColumn<Request>('type', 'Type'),
    createTextColumn<Request>('summary', 'Summary', { tooltip: true, tooltipMaxLength: 255 }),
    createStatusColumn<Request>('status', 'Status'),
    createTextColumn<Request>('requester', 'Requester', { minSize: 300, maxSize: 1000 }),
    createResponseTimeColumn<Request>('response_time'),
    createDateColumn<Request>('created_at', 'Created'),
];

// Sorted items
const sortedItems = computed(() => {
    return props.data ?? requests.value ?? [];
});

function makeQueryFromFilter(newFilter: RequestFilter): Record<string, string> {
    const query: Record<string, string> = {};

    if (newFilter.serviceDesks.length > 0) {
        query.serviceDesks = newFilter.serviceDesks.map((v) => v.value).join(',');
    }
    if (newFilter.requestTypes.length > 0) {
        query.requestTypes = newFilter.requestTypes.map((v) => v.value).join(',');
    }
    if (newFilter.statuses.length > 0) {
        query.statuses = newFilter.statuses.map((v) => v.value).join(',');
    }
    if (newFilter.otherFilters.length > 0) {
        query.otherFilters = newFilter.otherFilters.map((v) => v.value).join(',');
    }
    if (newFilter.referenceSearch.trim()) {
        query.referenceSearch = newFilter.referenceSearch.trim();
    }
    if (newFilter.summarySearch.trim()) {
        query.summarySearch = newFilter.summarySearch.trim();
    }

    return query;
}

function updateAndApplyFilter(newFilter: RequestFilter) {
    const query = makeQueryFromFilter(newFilter);
    router.replace({ name: 'support', query: query });
}

async function fetchRequests() {
    if (!selectedFilterData.value) return;

    try {
        const response = await supportStore.getRequests(
            selectedFilterData.value,
            null,
            props.itemCount ?? baseItemCount,
        );

        // Only update these if we got a valid response
        if (response) {
            totalRequests.value = response.total ?? 0;
            nextPageToken.value = response.nextPageToken ?? null;
            hasMoreData.value = !!response.nextPageToken;
            requests.value = translateRequestDatatoRequestType(response);
        }
    } catch (e) {
        console.error('Failed to fetch requests', e);
    }
}

async function updateFilter(filter: RequestFilter) {
    if (isFiltering.value) return;
    isFiltering.value = true;
    filterSetupComplete.value = true;
    try {
        selectedFilter.value = filter;

        nextPageToken.value = null;
        requests.value = [];
        hasMoreData.value = true;

        if (filter.serviceDesks.length === 0) {
            requests.value = [];
        } else {
            selectedFilterData.value = {
                serviceDeskId: filter.serviceDesks[0].value,
                requestTypes: filter.requestTypes.map((type) => `'${type.value}'`).join(','),
                statuses: filter.statuses.map((status) => `'${status.value}'`).join(','),
                showOrganizationRequests: filter.otherFilters.some((adminFilter) => adminFilter.value === 'Show all'),
                createdByUser: filter.otherFilters.some((adminFilter) => adminFilter.value === 'Created by me'),
                userIsParticipant: filter.otherFilters.some(
                    (adminFilter) => adminFilter.value === 'I am a participant',
                ),
                referenceSearch: filter.referenceSearch.trim() || undefined,
                summarySearch: filter.summarySearch.trim() || undefined,
            };
            requests.value = undefined;
            await fetchRequests();
        }
    } finally {
        isFiltering.value = false;
    }
}

// Polling logic
import { useIntervalFn } from '@vueuse/core';

// Only start polling if enabled. 60000ms = 1 minute.
const { pause, resume } = useIntervalFn(
    async () => {
        if (props.enablePolling && !isLoading.value && !isFiltering.value) {
            await fetchRequests();
        }
    },
    60000,
    { immediate: false },
);

watch(
    () => props.enablePolling,
    (shouldPoll) => {
        if (shouldPoll) {
            resume();
        } else {
            pause();
        }
    },
    { immediate: true },
);

async function loadMoreRequests() {
    if (!props.showFilters) return; // hack to disable loading more on the dashboard page

    if (!hasMoreData.value || isLoadingMore.value || !selectedFilterData.value || !nextPageToken.value) return;

    isLoadingMore.value = true;
    try {
        const response = await supportStore.getRequests(
            selectedFilterData.value,
            nextPageToken.value,
            props.itemCount ?? baseItemCount,
        );

        if (response) {
            const newRequests = translateRequestDatatoRequestType(response);
            requests.value = [...(requests.value || []), ...newRequests];
            nextPageToken.value = response.nextPageToken ?? null;
            hasMoreData.value = !!response.nextPageToken;
        }
    } finally {
        isLoadingMore.value = false;
    }
}

function setupIntersectionObserver() {
    if (observer) {
        observer.disconnect();
    }

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && hasMoreData.value && !isLoadingMore.value) {
                    loadMoreRequests();
                }
            });
        },
        {
            rootMargin: '200px',
        },
    );

    if (sentinelRef.value) {
        observer.observe(sentinelRef.value);
    }
}

function cleanupObserver() {
    if (observer) {
        observer.disconnect();
        observer = null;
    }
}

// Watch for sentinel ref changes to setup observer
watch(sentinelRef, async (newVal) => {
    if (newVal) {
        await nextTick();
        setupIntersectionObserver();
    }
});

// Cleanup on unmount
onUnmounted(() => {
    cleanupObserver();
});

// Initialize component
onMounted(async () => {
    const queryParams = route.query;

    // Check if any filters exist in the URL
    const hasExistingFilters =
        queryParams.serviceDesks ||
        queryParams.requestTypes ||
        queryParams.statuses ||
        queryParams.otherFilters ||
        queryParams.referenceSearch ||
        queryParams.summarySearch;

    const serviceDesk = await supportStore.getServiceDesks();
    if (serviceDesk === undefined) return;

    const defaultServiceDesk = serviceDesk.find((desk) => desk.service_desk_id === 74) ?? serviceDesk[0];

    if (hasExistingFilters) {
        selectedFilter.value = makeFilterFromQuery(queryParams);

        if (selectedFilter.value.serviceDesks.length === 0 && defaultServiceDesk) {
            selectedFilter.value.serviceDesks = [
                {
                    value: defaultServiceDesk.service_desk_id,
                    label: defaultServiceDesk.name,
                } as Option,
            ];
        }

        // Apply the filter from URL query params
        await updateFilter(selectedFilter.value);
    } else {
        // If no filters in URL, use the old logic to set the default filter

        if (defaultServiceDesk) {
            selectedFilter.value.serviceDesks = [
                {
                    value: defaultServiceDesk.service_desk_id,
                    label: defaultServiceDesk.name,
                } as Option,
            ];
        }

        if (props.showFilters) {
            // update the query params without triggering a loop or a watcher by using window instead of router
            const url = new URL(window.location.href);
            if (selectedFilter.value.serviceDesks.length > 0) {
                url.searchParams.set(
                    'serviceDesks',
                    selectedFilter.value.serviceDesks.map((desk) => desk.value).join(','),
                );
            }
            window.history.replaceState({}, '', url);
        }

        await updateFilter(selectedFilter.value);
    }
    isLoading.value = false;
});

function routeToDetails(request: Request) {
    router.push({ name: 'request-details', params: { requestId: request.id } });
}

function clear() {
    selectedFilter.value = {
        serviceDesks: [],
        requestTypes: [],
        statuses: [],
        otherFilters: [],
        referenceSearch: '',
        summarySearch: '',
    };

    updateAndApplyFilter(selectedFilter.value);
}

function makeFilterFromQuery(newQuery: LocationQuery): RequestFilter {
    const parseParam = (param: unknown): Option[] => {
        if (typeof param === 'string' && param.trim() !== '') {
            return param.split(',').map((value: string) => ({
                value,
                label: value,
            }));
        }
        return [];
    };

    return {
        serviceDesks: parseParam(newQuery.serviceDesks),
        requestTypes: parseParam(newQuery.requestTypes),
        statuses: parseParam(newQuery.statuses),
        otherFilters: parseParam(newQuery.otherFilters),
        referenceSearch: typeof newQuery.referenceSearch === 'string' ? newQuery.referenceSearch : '',
        summarySearch: typeof newQuery.summarySearch === 'string' ? newQuery.summarySearch : '',
    };
}

watch(
    () => route.query,
    async (newQuery) => {
        if (isLoading.value) return;
        const updatedFilter = makeFilterFromQuery(newQuery);
        selectedFilter.value = updatedFilter;
        // Trigger the filter update based on query params
        await updateFilter(updatedFilter);
    },
    { deep: true, immediate: true },
);

watch(
    () => sortedItems.value,
    () => {
        if (sortedItems.value.length === 0 && !isLoading.value) {
            emit('noResults');
        }
    },
);
</script>
